import { useState } from 'react';
import { Text, Spinner, Dropdown, Option, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { fetchAiUsage, type AiUsageSummary } from '../../api/admin.api';
import { useT } from '../../i18n';

const COLORS = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2', '#64748b'];
const CAUSES = ['timeout', 'rate_limited', 'no_credits', 'auth', 'server_error', 'other'] as const;

/** Etiqueta de causa traducida (claves aiCause_<causa> del namespace settings). */
function useCauseLabel() {
  const ts = useT().settings as unknown as Record<string, string>;
  return (cause: string) => ts[`aiCause_${cause}`] ?? cause;
}

/** Formatea tokens de forma compacta (1.2k, 3.4M). */
function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export function AiUsageSection() {
  const t = useT();
  const ts = t.settings;
  const causeLabel = useCauseLabel();
  const [days, setDays] = useState<7 | 30>(7);

  const { data, isLoading, isError } = useQuery<AiUsageSummary>({
    queryKey: ['ai-usage', days],
    queryFn: () => fetchAiUsage(days),
    staleTime: 60_000,
    retry: false,
  });

  const pie = (data?.byModel ?? []).map(m => ({
    name: `${m.provider} · ${m.model}`,
    value: m.calls,
    pct: m.sharePct,
    tokensIn: m.tokensIn,
    tokensOut: m.tokensOut,
  }));
  const failures = CAUSES
    .map(c => ({ cause: causeLabel(c), value: data?.failuresByCause[c] ?? 0 }))
    .filter(f => f.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <Text weight="semibold" style={{ fontSize: '12px' }}>{ts.aiUsageTitle}</Text>
        <Dropdown
          size="small"
          style={{ minWidth: 0, width: '160px' }}
          value={days === 7 ? ts.aiUsageDays7 : ts.aiUsageDays30}
          selectedOptions={[String(days)]}
          onOptionSelect={(_, d) => setDays(d.optionValue === '30' ? 30 : 7)}
        >
          <Option value="7">{ts.aiUsageDays7}</Option>
          <Option value="30">{ts.aiUsageDays30}</Option>
        </Dropdown>
      </div>

      {isLoading && <Spinner size="small" />}
      {isError && <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{ts.aiUsageLoadError}</Text>}
      {data && data.totals.calls === 0 && (
        <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{ts.aiUsageNoData}</Text>
      )}

      {data && data.totals.calls > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))', gap: '16px' }}>
          <div style={{ minWidth: 0 }}>
            <Text style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{ts.aiUsageShareTitle}</Text>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2}>
                    {pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number, _n, p: any) => [`${v} ${ts.aiUsageCalls} (${p?.payload?.pct}%) · ${ts.aiUsageTokens} ${fmtTokens(p?.payload?.tokensIn ?? 0)} / ${fmtTokens(p?.payload?.tokensOut ?? 0)}`, p?.payload?.name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '12px' }}>
              {(data.byModel).map((m, i) => (
                <li key={`${m.provider}#${m.model}`} style={{ display: 'flex', gap: '6px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length], display: 'inline-block' }} />
                  <span style={{ fontWeight: 600 }}>{m.provider} · {m.model}</span>
                  <span style={{ color: tokens.colorNeutralForeground3 }}>
                    {m.sharePct}% · {m.calls} {ts.aiUsageCalls} · {ts.aiUsageOk} {m.ok} / {ts.aiUsageFailed} {m.failed} · {ts.aiUsageAvgLatency} {m.avgLatencyMs} ms
                    {(m.tokensIn + m.tokensOut) > 0 && ` · ${ts.aiUsageTokens} ${fmtTokens(m.tokensIn)} / ${fmtTokens(m.tokensOut)}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div style={{ minWidth: 0 }}>
            <Text style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{ts.aiUsageFailuresTitle}</Text>
            {failures.length === 0 ? (
              <Text style={{ display: 'block', fontSize: '12px', marginTop: '8px' }}>0</Text>
            ) : (
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={failures} layout="vertical" margin={{ left: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="cause" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#dc2626" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
